const TENANT_OWNER = 'TENANT_OWNER'
const TENANT_SYSTEM_ADMIN = 'TENANT_SYSTEM_ADMIN'
const PLATFORM_SUPER_ADMIN = 'PLATFORM_SUPER_ADMIN'
const PLATFORM_OPERATIONS_ADMIN = 'PLATFORM_OPERATIONS_ADMIN'
const PLATFORM_SUPPORT_VIEWER = 'PLATFORM_SUPPORT_VIEWER'

const PROVIDER_ROLES = [
  PLATFORM_SUPER_ADMIN,
  PLATFORM_OPERATIONS_ADMIN,
  PLATFORM_SUPPORT_VIEWER
]

function values (context, key) {
  return context && Array.isArray(context[key]) ? context[key] : []
}

function hasAll (actual, required) {
  return (required || []).every(value => actual.includes(value))
}

function hasAny (actual, required) {
  return !(required || []).length || required.some(value => actual.includes(value))
}

function normalizePath (value) {
  const path = String(value || '')
    .split('?')[0]
    .split('#')[0]
    .replace(/\/+$/, '')
    .toLowerCase()
  return path || '/'
}

function isProviderEmergencyAdmin (context) {
  return values(context, 'roles').includes(PLATFORM_SUPER_ADMIN)
}

function isProviderOperationsAdmin (context) {
  return values(context, 'roles').includes(PLATFORM_OPERATIONS_ADMIN)
}

function isProviderSupportViewer (context) {
  return values(context, 'roles').includes(PLATFORM_SUPPORT_VIEWER)
}

function hasProviderPermission (context, permissionCode) {
  return values(context, 'permissions').includes(permissionCode)
}

export function hasProviderRole (context) {
  return values(context, 'roles').some(role => PROVIDER_ROLES.includes(role))
}

export function canManageProviderRoles (context) {
  return isProviderEmergencyAdmin(context)
}

export function canOpenProviderAccessManagement (context) {
  if (isProviderEmergencyAdmin(context)) return true
  if (!hasProviderRole(context)) return false
  return hasProviderPermission(context, 'ACCOUNT_VIEW') ||
    hasProviderPermission(context, 'CONTRACT_VIEW')
}

/**
 * プランの料金を変えられるか。
 *
 * **料金は全社に効く。**1社ぶんの契約を直すのとは重さが違うので、
 * 請求の参照権では書かせない。back も PLAN_CHANGE を要求する。
 */
export function canChangeProviderPlan (context) {
  if (isProviderEmergencyAdmin(context)) return true
  return isProviderOperationsAdmin(context) &&
    hasProviderPermission(context, 'PLAN_CHANGE')
}

/**
 * 月次の請求一覧を開けるか。
 *
 * **参照だけならサポートにも開く。**金額と入金の状況が見えないと、
 * 「払ったのに止まった」という問い合わせに答えられない。
 *
 * <b>経路表（POLICIES）から呼ぶ。</b>画面の中で判定しても、
 * URL を直打ちされたら通ってしまう。
 */
export function canOpenProviderBilling (context) {
  if (isProviderEmergencyAdmin(context)) return true
  if (!hasProviderRole(context)) return false
  return hasProviderPermission(context, 'PROVIDER_BILLING_VIEW')
}

/**
 * 請求書を発行できるか。
 *
 * **番号を採る操作なので、採ったら取り消せない。**
 * 一覧を見るだけの人（サポート）には出さない。
 */
export function canIssueProviderInvoice (context) {
  if (isProviderEmergencyAdmin(context)) return true
  return isProviderOperationsAdmin(context) &&
    hasProviderPermission(context, 'PROVIDER_INVOICE_ISSUE')
}

/**
 * お知らせ配信と祝日マスタを開けるか（2026-09-30、束7・お勧めで決めたこと 17）。
 *
 * **運営管理者にも開く。**前は緊急最高管理者だけで、年に1回の祝日の取り込みや
 * メンテナンスの予告のたびに、運営管理者が緊急用の役割で入り直していた。
 * 運営管理者は PROVIDER_NOTICE_HOLIDAY_MANAGE（migration 0118）を持つときだけ。
 * サポート閲覧者（参照だけ）と会社側の人には開かない。back の経路表
 * （ApiAccessPolicyService の /platform-notices・/platform-holidays）も同じ判定。
 */
export function canManageProviderNoticesAndHolidays (context) {
  if (isProviderEmergencyAdmin(context)) return true
  return isProviderOperationsAdmin(context) &&
    hasProviderPermission(context, 'PROVIDER_NOTICE_HOLIDAY_MANAGE')
}

export function canCreateProviderAccount (context) {
  if (isProviderEmergencyAdmin(context)) return true
  return isProviderOperationsAdmin(context) &&
    hasProviderPermission(context, 'ACCOUNT_CREATE')
}

export function canUpdateProviderContract (context) {
  if (isProviderEmergencyAdmin(context)) return true
  return isProviderOperationsAdmin(context) &&
    hasProviderPermission(context, 'CONTRACT_UPDATE')
}

export function canUpdateProviderFeatures (context) {
  if (isProviderEmergencyAdmin(context)) return true
  return isProviderOperationsAdmin(context) &&
    hasProviderPermission(context, 'FEATURE_MANAGE')
}

export function canManageInitialOwnerInvitation (context) {
  if (isProviderEmergencyAdmin(context)) return true
  return isProviderOperationsAdmin(context) &&
    hasProviderPermission(context, 'ACCOUNT_UPDATE')
}

export function isProviderReadOnly (context) {
  return isProviderSupportViewer(context) &&
    !isProviderEmergencyAdmin(context) &&
    !isProviderOperationsAdmin(context)
}

export function isTenantOwner (context) {
  return values(context, 'roles').includes(TENANT_OWNER)
}

export function isTenantSystemAdmin (context) {
  return values(context, 'roles').includes(TENANT_SYSTEM_ADMIN)
}

export function canOpenTenantAccessManagement (context) {
  if (isTenantOwner(context)) return true
  return isTenantSystemAdmin(context) &&
    hasAll(values(context, 'permissions'), ['ROLE_VIEW', 'PERMISSION_VIEW'])
}

/**
 * 自社の請求を開けるか。
 *
 * **役割と機能の両方を見る。**金額と支払方法を一般の利用者が
 * 変えられると困るので、会社の代表者（TENANT_OWNER）と
 * システム責任者（TENANT_SYSTEM_ADMIN）だけ。
 *
 * 機能フラグ BILLING_VIEW は運営が会社ごとに切る。**代行を決めて
 * いない支払方法をメニューへ出さないため**の仕組みで、
 * back の ApiAccessPolicyService も同じ2つを見ている。
 */
export function canOpenTenantBilling (context) {
  if (!values(context, 'features').includes('BILLING_VIEW')) return false
  return isTenantOwner(context) || isTenantSystemAdmin(context)
}

export function canEditTenantRoles (context) {
  if (isTenantOwner(context)) return true
  return isTenantSystemAdmin(context) &&
    hasAll(values(context, 'permissions'), ['ROLE_MANAGE', 'PERMISSION_VIEW'])
}

export function canAssignTenantRoles (context) {
  if (isTenantOwner(context)) return true
  return isTenantSystemAdmin(context) &&
    values(context, 'permissions').includes('ROLE_ASSIGN')
}

export function canManageProtectedTenantAdminRoles (context) {
  return isTenantOwner(context)
}

/** 削除・所属変更・通知・金額などのプロジェクト管理操作。 */
export function canManageProjectAdministration (context) {
  return values(context, 'features').includes('PROJECT') &&
    values(context, 'permissions').includes('PROJECT_MANAGE')
}

/**
 * 取引先向けの請求書（請求書管理）の、出力（PDF・ZIP）と発行。
 *
 * back の経路表（ApiAccessPolicyService の /sales/invoice/ 以下）と同じ条件
 * （契約に請求書の機能 INVOICE があり、権限「請求書の管理」INVOICE_MANAGE を持つ）。
 *
 * 請求書管理の画面そのものは、「売上情報の管理」だけでも開ける（下の prefix
 * '/admin/sales'）。だが、請求書を出す口・発行する口はこの条件の人しか通らない。
 * 画面はこの判定で、押しても必ず断られるボタン（［請求書］［一括ダウンロード］
 * ［発行］［一括発行］）と、その使い方の説明を出し分ける。
 * 権限と契約を確かめられない（context が無い）ときは false。
 */
export function canOutputAndIssueSalesInvoice (context) {
  return values(context, 'features').includes('INVOICE') &&
    values(context, 'permissions').includes('INVOICE_MANAGE')
}

/**
 * 請求書管理の、売上の行の登録・更新・作り直し。
 *
 * back の経路表（ApiAccessPolicyService の /sales/register・/sales/update・
 * /sales/recreate）と同じ条件（契約に売上の機能 SALES があり、権限「売上情報の管理」
 * SALES_MANAGE を持つ）。
 *
 * 上の canOutputAndIssueSalesInvoice の逆向き。請求書管理の画面は「請求書の管理」
 * だけでも開けるが、売上の行を書く口はこの条件の人しか通らない。画面はこの判定で、
 * 押しても必ず断られるボタン（［登録］［更新］［最新を取り込んで再作成］）と、
 * その入力の表（新規登録・更新）への入口を出し分ける。
 * 権限と契約を確かめられない（context が無い）ときは false。
 */
export function canEditSalesRecords (context) {
  return values(context, 'features').includes('SALES') &&
    values(context, 'permissions').includes('SALES_MANAGE')
}

const POLICIES = [
  {
    prefix: '/platform/provider-role-management',
    custom: canManageProviderRoles
  },
  {
    /*
     * 全社のダッシュボードに出るお知らせ。**書けるのは運営だけ。**
     * 緊急最高管理者と、権限を持つ運営管理者（2026-09-30、束7）。
     */
    prefix: '/platform/notices',
    custom: canManageProviderNoticesAndHolidays
  },
  {
    /*
     * 全社共通の祝日マスタ。**触れるのは運営だけ。**
     *
     * ここが埋まっていれば契約企業は祝日を入力しなくてよい。
     * 逆に、切れた翌日から全社の祝日が平日になる。
     * 緊急最高管理者と、権限を持つ運営管理者（2026-09-30、束7）。
     */
    prefix: '/platform/national-holidays',
    custom: canManageProviderNoticesAndHolidays
  },
  {
    prefix: '/platform/access-management',
    custom: canOpenProviderAccessManagement
  },
  {
    /*
     * 運営の請求管理。**ここが抜けていた。**
     *
     * canAccessPath は経路表に無い道を通す作りなので、載せるまで
     * 誰でも開けた。back が既定拒否なのでデータは漏れないが、
     * 枠と403だけが見える画面になる——同じ経路表に自分たちで
     * 「開けるのに何もできない画面を作らない」と書いてある。
     */
    prefix: '/platform/billing',
    custom: canOpenProviderBilling
  },
  {
    /*
     * 入金・売掛管理。**請求と同じものを別の切り口で並べ直しただけ**
     * なので、開ける人は請求管理と同じ。
     *
     * 載せ忘れると誰でも開ける（この表に無い道は通る作り）。
     * back は既定拒否なのでデータは出ないが、枠と403だけの画面に
     * なって、利用者からは壊れているのと区別が付かない。
     */
    prefix: '/platform/receivables',
    custom: canOpenProviderBilling
  },
  {
    prefix: '/admin/tenant-access-management',
    custom: canOpenTenantAccessManagement
  },
  {
    /*
     * 端末を失くした社員の2要素を外す画面。
     *
     * **開けるのに何もできない画面を作らない。**back は
     * /tenant-access-management を TENANT_OWNER と TENANT_SYSTEM_ADMIN
     * だけに絞っているので中身は漏れないが、権限の無い人が開くと
     * 空の一覧と403だけが出る。利用者からは壊れているのと区別が付かない。
     *
     * 権限管理と同じ判定にそろえる。
     */
    prefix: '/admin/mfa-recovery',
    custom: canOpenTenantAccessManagement
  },
  {
    // 自社のご請求。役割（代表者・システム責任者）と機能フラグの両方。
    prefix: '/admin/billing',
    custom: canOpenTenantBilling,
    // 個別の判定の中で見ている機能。開けない理由を案内するときに使う（2026-09-22 監査）
    requiredFeatures: ['BILLING_VIEW']
  },
  {
    /*
     * 稼働状況分析。経営集計・分析（MANAGEMENT_INSIGHT。プレミアムだけ）の画面。
     *
     * 事務担当者の WORKFORCE_VIEW でも開ける（経営予測とは分けてある）が、
     * **会社で経営集計・分析が ON のときだけ。**前は勤怠・打刻の機能を条件に
     * していたので、スタンダード・ビジネスでもメニューに出て開けた（2026-09-26）。
     * 機能を条件にすると、開けない人への案内も「契約の機能」の理由になる。
     * back の互換の口（LegacyAuthorityCompatibilityService）も同じ組み合わせで断る。
     */
    prefix: '/admin/insights/workforce',
    featuresAll: ['MANAGEMENT_INSIGHT'],
    permissionsAny: ['MANAGEMENT_INSIGHT_VIEW', 'WORKFORCE_VIEW']
  },
  {
    /*
     * 36協定アラート。**稼働状況分析より狭くする。**
     *
     * 隣の /admin/insights/workforce は WORKFORCE_VIEW でも通るので、
     * 同じ条件にすると事務担当者にも見える。この画面は氏名つきで
     * 「誰が働きすぎているか」を出すため、経営管理者以上に限る。
     * back の ApiAccessPolicyService も同じ組み合わせで断る。
     */
    prefix: '/admin/insights/overtime-limits',
    /*
     * **労務法令の判定は、出してよい段階になってから。**
     *
     * 変形労働時間制・フレックスタイム制・週44時間の特例・日跨ぎ勤務を
     * 扱えていないあいだ、その制度を採っている会社には間違った数字が出る。
     * back が機能一覧へ載せるまで、メニューもコマンドパレットも
     * 直接URLも通さない。**画面ごとに「フラグを見る」を書き足すと、
     * 書き忘れた口から漏れる。**
     */
    featuresAll: ['MANAGEMENT_INSIGHT', 'OCCUPATIONAL_LEGAL_COMPLIANCE'],
    permissionsAny: ['MANAGEMENT_INSIGHT_VIEW']
  },
  {
    prefix: '/admin/insights/project-profit',
    featuresAll: ['MANAGEMENT_INSIGHT'],
    permissionsAny: ['MANAGEMENT_INSIGHT_VIEW']
  },
  {
    prefix: '/admin/insights/cash-flow',
    featuresAll: ['MANAGEMENT_INSIGHT'],
    permissionsAny: ['MANAGEMENT_INSIGHT_VIEW']
  },
  {
    prefix: '/admin/total/list_project',
    featuresAll: ['MONTHLY_AGGREGATION'],
    permissionsAny: ['MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/total/list',
    featuresAll: ['MONTHLY_AGGREGATION'],
    permissionsAny: ['MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/project-rate',
    featuresAll: ['PROJECT'],
    permissionsAny: ['PROJECT_MANAGE']
  },
  {
    prefix: '/admin/sales',
    featuresAny: ['SALES', 'INVOICE'],
    permissionsAny: ['SALES_MANAGE', 'INVOICE_MANAGE']
  },
  {
    prefix: '/admin/roster',
    featuresAll: ['ATTENDANCE'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/monthlyreports',
    featuresAll: ['ATTENDANCE'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/payoff',
    featuresAll: ['EXPENSE'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/paidandsubstitute',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/overtime',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/holidaywork',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/submission_approval/submission_management',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['APPROVAL_MANAGE', 'APPROVAL_USE_ASSIGNED']
  },
  {
    prefix: '/submission_approval/report_list',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['REQUEST_USE_SELF']
  },
  {
    prefix: '/report/roster',
    featuresAll: ['ATTENDANCE'],
    permissionsAny: ['ATTENDANCE_USE_SELF']
  },
  {
    prefix: '/report/monthlyreport',
    featuresAll: ['ATTENDANCE'],
    permissionsAny: ['ATTENDANCE_USE_SELF']
  },
  {
    prefix: '/payoff',
    featuresAll: ['EXPENSE'],
    permissionsAny: ['EXPENSE_USE_SELF']
  },
  {
    prefix: '/paidandsubstitutes',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['REQUEST_USE_SELF']
  },
  {
    prefix: '/overtimes',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['REQUEST_USE_SELF']
  },
  {
    prefix: '/holidayworks',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['REQUEST_USE_SELF']
  },
  {
    prefix: '/insights/paid-leave',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['REQUEST_USE_SELF']
  },
  {
    prefix: '/users',
    featuresAll: ['USER_MANAGEMENT'],
    permissionsAll: ['USER_MANAGE', 'ROLE_MANAGE']
  },
  {
    prefix: '/setting/project-notifications',
    featuresAll: ['PROJECT'],
    permissionsAny: ['PROJECT_MANAGE']
  },
  {
    prefix: '/setting',
    featuresAll: ['SYSTEM_SETTING'],
    permissionsAny: ['SYSTEM_SETTING_MANAGE']
  },
  {
    // 横断検索で本人に返すのは、現在所属するプロジェクトだけ。
    // Backendの /management/projects/{id} もPROJECT Feature＋本人所属境界で保護する。
    prefix: '/project/search',
    featuresAll: ['PROJECT']
  },
  {
    // 一覧・詳細・登録・更新は一般社員用PROJECT_EDITでも利用できる。
    // 削除・所属変更・通知・金額は各画面とBackendでPROJECT_MANAGEに分離する。
    prefix: '/project',
    featuresAll: ['PROJECT'],
    permissionsAny: ['PROJECT_EDIT', 'PROJECT_MANAGE']
  },
  {
    // 旧common / clerkも取引先の登録・更新・削除を利用していた。
    prefix: '/client',
    featuresAll: ['CLIENT'],
    permissionsAny: ['CLIENT_MANAGE']
  },
  {
    // 一般社員は旧common互換として一覧・組織図だけを参照できる。
    prefix: '/department/list',
    featuresAll: ['USER_MANAGEMENT'],
    permissionsAny: ['DEPARTMENT_VIEW', 'USER_MANAGE']
  },
  {
    prefix: '/department/pic',
    featuresAll: ['USER_MANAGEMENT'],
    permissionsAny: ['DEPARTMENT_VIEW', 'USER_MANAGE']
  },
  {
    // 登録・詳細・更新はUSER_MANAGE保持者だけに維持する。
    prefix: '/department',
    featuresAll: ['USER_MANAGEMENT'],
    permissionsAny: ['USER_MANAGE']
  }
]

/*
 * **割り当てられた承認者が、自分宛ての書類の詳細を開く画面**（束5、2026-09-30）。
 *
 * 承認待ちの申請で提出物名を押すと、下の詳細の画面へ飛ぶ。一般社員や社内システム
 * 管理者の承認者は、全社の一覧を開ける権限（APPROVAL_MANAGE か MONTHLY_AGGREGATION_VIEW）
 * を持たないので、業務報告・勤務表・休暇申請の詳細は「開けない」案内になり、
 * 中身を見ないまま承認するしかなかった。
 *
 * - ここで決めるのは「詳細の画面を開いてよいか」だけ。**一覧（…/list）は開かないまま。**
 * - **誰宛ての書類かは、画面では分からない。**読んでよい書類かは back（互換の口と
 *   URL の参照ID）が1件ずつ決め、他人宛ての書類の URL は断られる。
 * - 番号は数字か参照ID（UUID）だけに当てる。一覧・件数・PDF の道には当たらない。
 * - 経費精算・通勤費精算の詳細（/expenseList/…、/travelCostList/…）は、もともと
 *   この表のどれにも当たらない道なので、ここには足さない。
 */
const DETAIL_ID = '([0-9]+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})'
const ASSIGNED_APPROVER_DETAIL_POLICIES = [
  {
    // 業務報告詳細：/admin/monthlyreports/:id/:user_id/:year/:month（小文字にした形）
    pattern: new RegExp(`^/admin/monthlyreports/${DETAIL_ID}/${DETAIL_ID}/[0-9]{4}/[0-9]{1,2}$`),
    featuresAll: ['ATTENDANCE'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW', 'APPROVAL_USE_ASSIGNED']
  },
  {
    // 勤務表詳細：/admin/roster/:user_id/:year/:month/:user_name（参照IDの形は氏名なし）
    pattern: new RegExp(`^/admin/roster/${DETAIL_ID}/[0-9]{4}/[0-9]{1,2}(/[^/]+)?$`),
    featuresAll: ['ATTENDANCE'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW', 'APPROVAL_USE_ASSIGNED']
  },
  {
    // 休暇申請詳細（管理者）：/admin/paidandsubstitute/:id
    pattern: new RegExp(`^/admin/paidandsubstitute/${DETAIL_ID}$`),
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW', 'APPROVAL_USE_ASSIGNED']
  }
]

function matchesPrefix (path, prefix) {
  return path === prefix || path.startsWith(prefix + '/')
}

function decodedPath (path) {
  try {
    return decodeURI(path)
  } catch (error) {
    return path
  }
}

export function accessPolicyForPath (pathValue) {
  const path = normalizePath(pathValue)
  const detail = ASSIGNED_APPROVER_DETAIL_POLICIES
    .find(policy => policy.pattern.test(decodedPath(path)))
  if (detail) return detail
  return POLICIES.find(policy => matchesPrefix(path, policy.prefix)) || null
}

/**
 * 一覧の［承認取消］を押せるか（束5、2026-09-30）。
 *
 * **back の経路表（ApiAccessPolicyService の /submission/revocation_of_approval）と同じ判定。**
 * 一覧を開ける人（APPROVAL_MANAGE か MONTHLY_AGGREGATION_VIEW）で、会社で申請の機能が
 * 有効なとき。旧 clerk・旧 admin は一覧から取消していた。一般社員の割り当て承認
 * （APPROVAL_USE_ASSIGNED）には広げない。押せない人にはボタンを出さない。
 */
export function canRevokeApproval (context) {
  if (!context) return false
  return values(context, 'features').includes('WORKFLOW') &&
    hasAny(values(context, 'permissions'), ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW'])
}

export function canAccessPath (context, pathValue) {
  if (!context) return false
  const policy = accessPolicyForPath(pathValue)
  if (!policy) return true
  if (typeof policy.custom === 'function') return Boolean(policy.custom(context))

  const roles = values(context, 'roles')
  const features = values(context, 'features')
  const permissions = values(context, 'permissions')

  return hasAny(roles, policy.rolesAny) &&
    hasAll(roles, policy.rolesAll) &&
    hasAny(features, policy.featuresAny) &&
    hasAll(features, policy.featuresAll) &&
    hasAny(permissions, policy.permissionsAny) &&
    hasAll(permissions, policy.permissionsAll)
}

/**
 * **開けない理由を1つ返す**（2026-09-22）。案内の画面が、理由ごとに頼む相手を書き分けるため。
 *
 *   'PROVIDER' … 運営専用の画面を、運営の役割の無い人が開いた。社内の管理者に頼んでも開けない
 *   'FEATURE'  … 会社でその機能が有効になっていない。本人の権限を足しても開けない
 *   'ROLE'     … 本人に権限が付いていない
 *
 * **画面が確かめられる範囲だけを言う**（2026-09-22 監査）。会社の機能は、契約プランで
 * 足りないのか運営が会社ごとに止めたのかを画面側では区別できないので、「プランに
 * 含まれていない」とは言わない。個別の判定（custom）の中で機能を見ている画面は、
 * requiredFeatures に同じ機能を書く（書き忘れは検査が落とす）。
 */
export function accessDeniedReason (context, pathValue) {
  const path = normalizePath(pathValue)
  if (matchesPrefix(path, '/platform') && !hasProviderRole(context)) return 'PROVIDER'
  const policy = accessPolicyForPath(path)
  if (!policy) return 'ROLE'
  const features = values(context, 'features')
  const companyHasIt = hasAny(features, policy.featuresAny) &&
    hasAll(features, policy.featuresAll) &&
    hasAll(features, policy.requiredFeatures)
  return companyHasIt ? 'ROLE' : 'FEATURE'
}

/** 検査用：個別の判定を使う画面と、書いてある必要な機能の一覧。 */
export function customPoliciesForCheck () {
  return POLICIES.filter(policy => typeof policy.custom === 'function')
    .map(policy => ({ prefix: policy.prefix, custom: policy.custom, requiredFeatures: policy.requiredFeatures || [] }))
}

function filterChildren (children, context) {
  const candidates = (children || []).reduce((result, item) => {
    if (!item) return result
    if (item.to && !canAccessPath(context, item.to)) return result

    const clone = { ...item }
    if (Array.isArray(item._children) && item._name !== 'CSidebarNavTitle') {
      clone._children = filterChildren(item._children, context)
      if (!clone._children.length) return result
    }
    result.push(clone)
    return result
  }, [])

  return candidates.filter((item, index) => {
    if (item._name !== 'CSidebarNavTitle') return true
    for (let cursor = index + 1; cursor < candidates.length; cursor += 1) {
      if (candidates[cursor]._name === 'CSidebarNavTitle') return false
      return true
    }
    return false
  })
}

export function filterNavigation (navigation, context) {
  return (navigation || []).reduce((result, item) => {
    if (!item) return result
    const clone = { ...item }
    if (Array.isArray(item._children)) {
      clone._children = filterChildren(item._children, context)
      if (!clone._children.length) return result
    }
    result.push(clone)
    return result
  }, [])
}

export function presentationAuthority (context) {
  const roles = values(context, 'roles')
  const permissions = values(context, 'permissions')
  if (roles.some(role => [
    ...PROVIDER_ROLES,
    TENANT_OWNER,
    TENANT_SYSTEM_ADMIN,
    'EXECUTIVE_ADMIN'
  ].includes(role))) return 'admin'

  if (roles.includes('CLERK') || permissions.some(permission => [
    'MONTHLY_AGGREGATION_VIEW',
    'MONTHLY_AGGREGATION_EXPORT'
  ].includes(permission))) return 'clerk'

  return 'common'
}
